import React, { useState, useEffect, useMemo } from 'react';
import { employeeApi, timeEntryApi } from '../../lib/api';
import { Department } from '../../lib/api/departments.api';
import Loader from '../common/Loader';
import Button from '../common/Button';

interface TimeEntry {
    id: string;
    employee: {
        id: string;
        name: string;
        dni: string;
    };
    type: 'IN' | 'OUT' | 'BREAK' | 'RESUME';
    timestamp: string;
    localTimestamp?: string; // Timestamp en zona horaria local
    source: string;
    createdByEmployee: boolean;
    breakTypeId?: string;
    breakType?: {
        id: string;
        name: string;
        color: string;
        description?: string;
        customName?: string;
        isCustom?: boolean;
    };
    breakReason?: string;
    // Campos de evaluación contra horario con periodo de gracia
    isOutOfSchedule?: boolean;
    lateMinutes?: number;
    earlyLeaveMinutes?: number;
    outOfScheduleReason?: string;
    scheduleId?: string;
}

interface Schedule {
    id: string;
    name: string;
    startTime: string;
    endTime: string;
    color?: string;
    isReference?: boolean; // Para diferenciar entre horarios asignados y predefinidos
    graceEnabled?: boolean;
    graceInBeforeStartMin?: number;
    graceInNoLateAfterStartMin?: number;
    graceInAfterStartMaxMin?: number;
    graceOutBeforeEndMin?: number;
    graceOutAfterEndMin?: number;
}

interface Employee {
    id: string;
    name: string;
    dni: string;
}

interface TimelineViewProps {
    from: string;
    to: string;
    employeeId?: string;
    departmentId?: string;
    departments?: Department[];
    employees?: Employee[];
}

const TimelineView: React.FC<TimelineViewProps> = ({ from, to, employeeId, departmentId, departments = [], employees = [] }) => {
    const [loading, setLoading] = useState(true);
    const [timeEntries, setTimeEntries] = useState<TimeEntry[]>([]);
    const [selectedEmployee, setSelectedEmployee] = useState<string>(employeeId || '');
    const [selectedDepartment, setSelectedDepartment] = useState<string>(departmentId || '');
    const [error, setError] = useState<string | null>(null);
    const [showOnlyDelays, setShowOnlyDelays] = useState(false);
    const [showOnlyOutsideSchedule, setShowOnlyOutsideSchedule] = useState(false);

    // Estados para el formulario de fichaje manual
    const [showManualPunch, setShowManualPunch] = useState(false);
    const [manualPunchData, setManualPunchData] = useState({
        employeeId: '',
        type: 'IN' as 'IN' | 'OUT',
        date: from,
        time: ''
    });
    const [submittingPunch, setSubmittingPunch] = useState(false);
    const [punchError, setPunchError] = useState<string | null>(null);
    const [continuingBreak, setContinuingBreak] = useState(false);
    const [continueBreakError, setContinueBreakError] = useState<string | null>(null);
    const [showContinueBreakModal, setShowContinueBreakModal] = useState(false);
    const [selectedBreakEntry, setSelectedBreakEntry] = useState<TimeEntry | null>(null);
    const [continueBreakTimestamp, setContinueBreakTimestamp] = useState('');

    // Generar horas del día (0-23)
    const hours = Array.from({ length: 24 }, (_, i) => i);

    // Agrupar registros por empleado y fecha usando useMemo para recalcular cuando cambien timeEntries
    const entriesByEmployeeAndDate = useMemo(() => {
        const grouped: { [employeeId: string]: { [date: string]: TimeEntry[] } } = {};

        timeEntries.forEach(entry => {
            const entryTime = new Date(entry.timestamp);
            const originalDate = entryTime.toISOString().split('T')[0];
            let entryDate = originalDate;

            if (!grouped[entry.employee.id]) {
                grouped[entry.employee.id] = {};
            }

            if (!grouped[entry.employee.id][entryDate]) {
                grouped[entry.employee.id][entryDate] = [];
            }

            grouped[entry.employee.id][entryDate].push(entry);
        });

        return grouped;
    }, [timeEntries]);

    // Actualizar los estados internos y cargar datos cuando cambian los props
    useEffect(() => {
        if (employeeId !== undefined) setSelectedEmployee(employeeId);
        if (departmentId !== undefined) setSelectedDepartment(departmentId);
    }, [employeeId, departmentId]);

    useEffect(() => {
        // Actualizar la fecha del formulario de fichaje manual cuando cambie 'from'
        if (from) {
            setManualPunchData(prev => ({ ...prev, date: from }));
        }
    }, [from]);

    useEffect(() => {
        if (from && to) {
            loadData();
        }
    }, [from, to]);

    const loadData = async () => {
        try {
            setLoading(true);
            setError(null);

            // Usar el rango de fechas from/to del filtro
            // Validar que las fechas no sean inválidas
            if (!from || !to) {
                setError('Fechas no válidas');
                setLoading(false);
                return;
            }

            const fromDate = new Date(from);
            const toDate = new Date(to);

            if (isNaN(fromDate.getTime()) || isNaN(toDate.getTime())) {
                setError('Fechas inválidas');
                setLoading(false);
                return;
            }

            // Backend espera fechas en formato YYYY-MM-DD
            const entriesParams: any = {
                from: from,
                to: to,
                limit: 100, // Límite máximo permitido por el backend
            };

            // Usar los filtros de props en lugar de los estados internos
            if (employeeId) {
                entriesParams.employeeId = employeeId;
            }

            if (departmentId) {
                entriesParams.departmentId = departmentId;
            }

            // Cargar todos los registros usando paginación
            let allEntries: TimeEntry[] = [];
            let page = 1;
            let hasMore = true;

            while (hasMore) {
                const response = await timeEntryApi.getTimeEntries({
                    ...entriesParams,
                    page,
                });

                if (response.success && response.data) {
                    const entries = response.data.timeEntries || [];
                    allEntries = [...allEntries, ...entries];

                    // Verificar si hay más páginas
                    const pagination = response.data.pagination;
                    if (pagination && allEntries.length < pagination.total) {
                        hasMore = true;
                        page++;
                    } else {
                        hasMore = false;
                    }
                } else {
                    hasMore = false;
                }
            }

            console.log('🔍 DEBUG loadData - Entries detallados:', allEntries.map(e => ({
                id: e.id,
                type: e.type,
                timestamp: e.timestamp,
                date: new Date(e.timestamp).toISOString().split('T')[0],
                hours: new Date(e.timestamp).getHours(),
                minutes: new Date(e.timestamp).getMinutes(),
                breakTypeId: e.breakTypeId,
                breakType: e.breakType,
                breakReason: e.breakReason
            })));
            setTimeEntries(allEntries);

            // Si hay un empleado seleccionado, cargar sus horarios del día
            if (selectedEmployee && selectedEmployee.trim() !== '') {
                try {
                                                            
                    // Primero, cargar los registros para saber qué fechas tienen fichajes
                    const entriesResponse = await timeEntryApi.getTimeEntries(entriesParams);
                    if (entriesResponse.success && entriesResponse.data) {
                        const allEntries = entriesResponse.data.timeEntries || [];
                        
                        // Obtener las fechas únicas que tienen fichajes
                        const uniqueDates = Array.from(new Set(
                            allEntries.map(entry => new Date(entry.timestamp).toISOString().split('T')[0])
                        )).sort();

                        
                        // Cargar horarios para cada fecha que tiene fichajes
                        const schedulesData: { [date: string]: Schedule[] } = {};

                        for (const dateStr of uniqueDates) {
                            try {
                                                                const scheduleResponse = await employeeApi.getDailySchedule(selectedEmployee, dateStr);
                                                                if (scheduleResponse.success && scheduleResponse.data) {
                                    const dateSchedules = (scheduleResponse.data as any).schedules || [];
                                                                        // Si el empleado no tiene horarios asignados, no mostrar ningún turno (descanso)
                                    schedulesData[dateStr] = dateSchedules;
                                }
                            } catch (scheduleError) {
                                                                // En caso de error, no mostrar ningún turno
                                schedulesData[dateStr] = [];
                            }
                        }


                        // Almacenar los horarios por fecha en una variable adicional
                        (window as any).schedulesByDate = schedulesData;
                    }
                } catch (scheduleError) {
                                        // En caso de error, no mostrar ningún turno
                }
            } else {
                // Si no hay empleado seleccionado, cargar horarios de todos los empleados
                const schedulesData: { [employeeId: string]: Schedule[] } = {};
                const schedulesByDateAndEmployee: { [employeeId: string]: { [date: string]: Schedule[] } } = {};

                // Primero, obtener los fichajes de todos los empleados
                const allEntriesResponse = await timeEntryApi.getTimeEntries(entriesParams);
                if (allEntriesResponse.success && allEntriesResponse.data) {
                    const allEntries = allEntriesResponse.data.timeEntries || [];
                    
                    // Procesar cada empleado
                    for (const employee of employees) {
                        try {
                            // Obtener los fichajes de este empleado para saber qué fechas tienen registros
                            const employeeEntries = allEntries.filter((e: TimeEntry) => e.employee.id === employee.id);

                            if (employeeEntries.length > 0) {
                                // Obtener las fechas únicas que tienen fichajes de este empleado
                                const uniqueDates = Array.from(new Set(
                                    employeeEntries.map((entry: TimeEntry) => new Date(entry.timestamp).toISOString().split('T')[0])
                                )).sort();

                                
                                // Cargar horarios para cada fecha que tiene fichajes
                                schedulesByDateAndEmployee[employee.id] = {};

                                // Usar Promise.all para cargar todos los horarios en paralelo
                                const schedulePromises = uniqueDates.map(async (dateStr) => {
                                    try {
                                                                                const scheduleResponse = await employeeApi.getDailySchedule(employee.id, dateStr);
                                                                                if (scheduleResponse.success && scheduleResponse.data) {
                                            const dateSchedules = (scheduleResponse.data as any).schedules || [];
                                                                                        // Si el empleado no tiene horarios asignados, no mostrar ningún turno (descanso)
                                            return { dateStr, schedules: dateSchedules };
                                        }
                                        return { dateStr, schedules: [] };
                                    } catch (scheduleError) {
                                                                                // En caso de error, no mostrar ningún turno
                                        return { dateStr, schedules: [] };
                                    }
                                });

                                // Esperar a que todas las llamadas se completen
                                const scheduleResults = await Promise.all(schedulePromises);

                                // Almacenar los resultados en schedulesByDateAndEmployee
                                scheduleResults.forEach(({ dateStr, schedules }) => {
                                    schedulesByDateAndEmployee[employee.id][dateStr] = schedules;
                                });

                                // Usar los horarios de la fecha seleccionada como horarios por defecto
                                const selectedDateSchedules = schedulesByDateAndEmployee[employee.id][from] || [];
                                schedulesData[employee.id] = selectedDateSchedules;
                            } else {
                                // Si el empleado no tiene fichajes, cargar los horarios de la fecha seleccionada
                                const scheduleResponse = await employeeApi.getDailySchedule(employee.id, from);
                                                                if (scheduleResponse.success && scheduleResponse.data) {
                                    const employeeSchedules = (scheduleResponse.data as any).schedules || [];
                                                                        // Si el empleado no tiene horarios asignados, no mostrar ningún turno (descanso)
                                    schedulesData[employee.id] = employeeSchedules;
                                }
                            }
                        } catch (scheduleError) {
                                                        // En caso de error, no mostrar ningún turno
                            schedulesData[employee.id] = [];
                        }
                    }
                }

                                                

                // Almacenar los horarios por fecha y por empleado en una variable adicional
                (window as any).schedulesByDateAndEmployee = schedulesByDateAndEmployee;
            }

        } catch (error: any) {
            setError(error.message || 'Error al cargar los datos');
        } finally {
            setLoading(false);
        }
    };

    // Convertir hora a posición en la línea de tiempo (porcentaje) usando hora UTC
    const timeToPositionUTC = (timeString: string): number => {
        const [hours, minutes] = timeString.split(':').map(Number);
        return ((hours * 60 + minutes) / (24 * 60)) * 100;
    };

    // Calcular horas brutas del día (basadas en turnos asignados)
    const calculateGrossHours = (schedules: Schedule[]): number => {
                
        let totalMinutes = 0;

        schedules.forEach((schedule, index) => {
            

            const [startHour, startMinute] = schedule.startTime.split(':').map(Number);
            const [endHour, endMinute] = schedule.endTime.split(':').map(Number);

            let startTimeMinutes = startHour * 60 + startMinute;
            let endTimeMinutes = endHour * 60 + endMinute;

            

            // Si el turno cruza medianoche
            if (endTimeMinutes < startTimeMinutes) {
                endTimeMinutes += 24 * 60; // Añadir 24 horas
                            }

            const duration = endTimeMinutes - startTimeMinutes;
            console.log(`🔍 DEBUG schedule ${index} - duration (minutos):`, duration);

            totalMinutes += Math.max(0, duration);
                    });

                return totalMinutes; // Devolver minutos, se convertirá a horas en formatMinutes
    };

    // Calcular horas netas del día (basadas en fichajes reales)
    const calculateNetHours = (entries: TimeEntry[]): number => {
        let totalMinutes = 0;
        let lastEntryTime: Date | null = null;
        let lastEntryType: string | null = null;

        const sortedEntries = [...entries].sort((a, b) =>
            new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
        );

        console.log('🔍 DEBUG calculateNetHours - entries recibidos:', entries.map(e => ({
            id: e.id,
            type: e.type,
            timestamp: e.timestamp,
            date: new Date(e.timestamp).toISOString().split('T')[0],
            hour: new Date(e.timestamp).getHours()
        })));

        for (const entry of sortedEntries) {
            const entryTime = new Date(entry.timestamp);

            console.log('🔍 DEBUG calculateNetHours - procesando entry:', {
                id: entry.id,
                type: entry.type,
                timestamp: entry.timestamp,
                lastEntryTime: lastEntryTime?.toISOString(),
                lastEntryType,
                totalMinutes
            });

            if (entry.type === 'IN') {
                // Si ya hay un IN pendiente, contar hasta este IN (cierre implícito)
                if (lastEntryTime && lastEntryType === 'IN') {
                    const duration = Math.round((entryTime.getTime() - lastEntryTime.getTime()) / (1000 * 60));
                                        totalMinutes += Math.max(0, duration);
                }
                lastEntryTime = entryTime;
                lastEntryType = 'IN';
            } else if (entry.type === 'OUT') {
                if (lastEntryTime && lastEntryType === 'IN') {
                    const duration = Math.round((entryTime.getTime() - lastEntryTime.getTime()) / (1000 * 60));
                                        totalMinutes += Math.max(0, duration);
                    lastEntryTime = null;
                    lastEntryType = null;
                } else {
                    // OUT sin IN previo, ignorar
                                    }
            } else if (entry.type === 'BREAK') {
                // Si hay un IN pendiente, contar hasta el BREAK
                if (lastEntryTime && lastEntryType === 'IN') {
                    const duration = Math.round((entryTime.getTime() - lastEntryTime.getTime()) / (1000 * 60));
                                        // Usar Math.ceil en lugar de Math.round para asegurar que se cuente al menos 1 minuto
                    totalMinutes += Math.max(1, Math.ceil(duration));
                    lastEntryTime = entryTime;
                    lastEntryType = 'BREAK';
                } else {
                    // BREAK sin IN previo, ignorar
                                        lastEntryTime = entryTime;
                    lastEntryType = 'BREAK';
                }
            } else if (entry.type === 'RESUME') {
                // RESUME marca el fin de la pausa, pero no añade tiempo trabajado
                if (lastEntryTime && lastEntryType === 'BREAK') {
                    lastEntryTime = entryTime;
                    lastEntryType = 'IN';
                                    } else {
                    // RESUME sin BREAK previo, tratar como IN
                    lastEntryTime = entryTime;
                    lastEntryType = 'IN';
                                    }
            }
        }

        // Si hay una entrada sin salida, contar hasta la hora actual
        if (lastEntryTime && lastEntryType === 'IN' && lastEntryTime < new Date()) {
            const duration = Math.round((new Date().getTime() - lastEntryTime.getTime()) / (1000 * 60));
                        totalMinutes += Math.max(0, duration);
        }

                return totalMinutes;
    };

    // Calcular total de tiempo de pausa
    const calculateBreakMinutes = (entries: TimeEntry[]): number => {
        let totalMinutes = 0;
        let lastBreakTime: Date | null = null;

        const sortedEntries = [...entries].sort((a, b) =>
            new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
        );

        for (const entry of sortedEntries) {
            const entryTime = new Date(entry.timestamp);

            if (entry.type === 'BREAK') {
                lastBreakTime = entryTime;
            } else if (entry.type === 'RESUME' && lastBreakTime) {
                const duration = Math.round((entryTime.getTime() - lastBreakTime.getTime()) / (1000 * 60));
                totalMinutes += Math.max(0, duration);
                lastBreakTime = null;
            }
        }

        // Si hay una pausa sin reanudar, contar hasta la hora actual
        if (lastBreakTime && lastBreakTime < new Date()) {
            const duration = Math.round((new Date().getTime() - lastBreakTime.getTime()) / (1000 * 60));
            totalMinutes += Math.max(0, duration);
        }

        return totalMinutes;
    };

    // Formatear minutos a horas y minutos
    const formatMinutes = (minutes: number): string => {
        const hours = Math.floor(minutes / 60);
        const mins = minutes % 60;
        return `${hours}h ${mins.toString().padStart(2, '0')}m`;
    };

    // Convertir timestamp a posición en la línea de tiempo
    const timestampToPosition = (timestamp: string): number => {
        const date = new Date(timestamp);
        // Usar hora local para mostrar correctamente los fichajes en la zona horaria del usuario
        const hours = date.getHours();
        const minutes = date.getMinutes();

        console.log('🔍 DEBUG timestampToPosition:', {
            timestamp,
            localHours: hours,
            localMinutes: minutes,
            utcHours: date.getUTCHours(),
            utcMinutes: date.getUTCMinutes(),
            timezoneOffset: date.getTimezoneOffset()
        });

        // Calcular posición basada en hora local
        const position = ((hours * 60 + minutes) / (24 * 60)) * 100;

        
        return position;
    };

    // Obtener tipo de fichaje
    const getTypeLabel = (type: string, breakType?: { name: string; color: string; customName?: string; isCustom?: boolean }) => {
        const types = {
            'IN': 'Entrada',
            'OUT': 'Salida',
            'BREAK': breakType ? (breakType.isCustom && breakType.customName ? breakType.customName : breakType.name) : 'Pausa',
            'RESUME': 'Reanudar',
        };
        return types[type as keyof typeof types] || type;
    };

    // Obtener color para tipo de fichaje
    const getTypeColor = (type: string) => {
        const colors = {
            'IN': 'bg-green-500',
            'OUT': 'bg-red-500',
            'BREAK': 'bg-yellow-500',
            'RESUME': 'bg-blue-500',
        };
        return colors[type as keyof typeof colors] || 'bg-gray-500';
    };

    // Determinar si un horario cruza medianoche
    const scheduleCrossesMidnight = (schedule: Schedule): boolean => {
        const [startHour] = schedule.startTime.split(':').map(Number);
        const [endHour] = schedule.endTime.split(':').map(Number);
        return endHour < startHour || (endHour === startHour && schedule.endTime < schedule.startTime);
    };

    // Función auxiliar para verificar si un fichaje tiene retraso
    const hasEntryDelay = (entry: TimeEntry): boolean => {
        if (entry.type !== 'IN') return false;
        return (entry.lateMinutes || 0) > 0;
    };

    const isEntryOutsideSchedule = (entry: TimeEntry): boolean => {
        return entry.isOutOfSchedule || false;
    };

    // Función auxiliar para verificar si una fecha tiene retrasos
    const dateHasDelays = (employeeId: string, dateString: string): boolean => {
        const entriesForDate = entriesByEmployeeAndDate[employeeId]?.[dateString] || [];
        return entriesForDate.some(entry => hasEntryDelay(entry));
    };

    // Función auxiliar para verificar si una fecha tiene fichajes fuera de horario
    const dateHasOutsideSchedule = (employeeId: string, dateString: string): boolean => {
        const entriesForDate = entriesByEmployeeAndDate[employeeId]?.[dateString] || [];
        return entriesForDate.some(entry => isEntryOutsideSchedule(entry));
    };

    // Función auxiliar para verificar si un empleado tiene retrasos en alguna fecha
    const employeeHasDelays = (employeeId: string): boolean => {
        const employeeEntries = entriesByEmployeeAndDate[employeeId] || {};
        return Object.keys(employeeEntries).some(dateString => dateHasDelays(employeeId, dateString));
    };

    // Función auxiliar para verificar si un empleado tiene fichajes fuera de horario en alguna fecha
    const employeeHasOutsideSchedule = (employeeId: string): boolean => {
        const employeeEntries = entriesByEmployeeAndDate[employeeId] || {};
        return Object.keys(employeeEntries).some(dateString => dateHasOutsideSchedule(employeeId, dateString));
    };

    // Obtener empleados a mostrar
    // Solo mostrar empleados que tienen registros
    const employeeIdsWithEntries = new Set(timeEntries.map(entry => entry.employee.id));
    const employeesToShow = (employeeId || selectedEmployee)
        ? employees.filter(emp => emp.id === (employeeId || selectedEmployee))
        : employees.filter(emp => {
            // Si hay filtros activos, verificar si el empleado cumple con los filtros
            if (showOnlyDelays && !employeeHasDelays(emp.id)) {
                return false;
            }
            if (showOnlyOutsideSchedule && !employeeHasOutsideSchedule(emp.id)) {
                return false;
            }
            return employeeIdsWithEntries.has(emp.id);
        });

    // Obtener fechas únicas para mostrar
    const getUniqueDates = (employeeId: string): string[] => {
        const employeeEntries = entriesByEmployeeAndDate[employeeId] || {};
        const allDates = Object.keys(employeeEntries).sort();

        // Si hay filtros activos, filtrar fechas
        if (showOnlyDelays || showOnlyOutsideSchedule) {
            return allDates.filter(dateString => {
                // Si el filtro de retrasos está activo, verificar si la fecha tiene retrasos
                if (showOnlyDelays && !dateHasDelays(employeeId, dateString)) {
                    return false;
                }
                // Si el filtro de fuera de horario está activo, verificar si la fecha tiene fichajes fuera de horario
                if (showOnlyOutsideSchedule && !dateHasOutsideSchedule(employeeId, dateString)) {
                    return false;
                }
                return true;
            });
        }

        return allDates;
    };

    // Formatear fecha para mostrar
    const formatDate = (dateString: string): string => {
        const date = new Date(dateString + 'T00:00:00');
        const options: Intl.DateTimeFormatOptions = {
            weekday: 'long',
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        };
        return date.toLocaleDateString('es-ES', options);
    };

    // Determinar si una fecha es la fecha seleccionada
    const isSelectedDate = (dateString: string): boolean => {
        return dateString === from;
    };

    // Función para manejar el fichaje manual
    const handleManualPunch = async () => {
        if (!manualPunchData.employeeId || !manualPunchData.time) {
            setPunchError('Por favor, complete todos los campos');
            return;
        }

        try {
            setSubmittingPunch(true);
            setPunchError(null);

            // Combinar fecha y hora para crear el timestamp
            const dateTime = new Date(`${manualPunchData.date}T${manualPunchData.time}`);
            const timestamp = dateTime.toISOString();

            const punchData = {
                employeeId: manualPunchData.employeeId,
                type: manualPunchData.type,
                timestamp,
                source: 'ADMIN',
                createdByEmployee: false
            };

            const response = await timeEntryApi.createTimeEntry(punchData);

            if (response.success) {
                // Cerrar el formulario y recargar los datos
                setShowManualPunch(false);
                setManualPunchData({
                    employeeId: '',
                    type: 'IN',
                    date: from,
                    time: ''
                });
                loadData(); // Recargar los datos para mostrar el nuevo fichaje
            } else {
                setPunchError(response.message || 'Error al crear el fichaje');
            }
        } catch (error: any) {
            setPunchError(error.message || 'Error al crear el fichaje');
        } finally {
            setSubmittingPunch(false);
        }
    };

    // Función para abrir el formulario de fichaje manual
    const openManualPunchForm = (employeeId?: string) => {
        setManualPunchData({
            employeeId: employeeId || '',
            type: 'IN',
            date: from,
            time: ''
        });
        setShowManualPunch(true);
        setPunchError(null);
    };

    // Función para continuar una pausa existente
    const handleContinueBreak = async (entry: TimeEntry, timestamp?: string) => {
        if (!entry.employee) {
            setContinueBreakError('No se puede continuar la pausa: empleado no encontrado');
            return;
        }

        try {
            setContinuingBreak(true);
            setContinueBreakError(null);

            await timeEntryApi.continueBreak({
                employeeId: entry.employee.id,
                timestamp: timestamp || new Date().toISOString(),
                source: 'ADMIN',
                notes: 'Reanudación manual de pausa',
            });

            await loadData();
            setShowContinueBreakModal(false);
            setSelectedBreakEntry(null);
        } catch (error: any) {
            setContinueBreakError(error.message || 'Error al continuar la pausa');
        } finally {
            setContinuingBreak(false);
        }
    };

    if (loading) {
        return (
            <div className="flex justify-center py-8">
                <Loader size="lg" />
            </div>
        );
    }

    if (error) {
        return (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
                {error}
            </div>
        );
    }

    return (
        <div className="space-y-4">
            {/* Selector de departamento y empleado y botón de fichaje manual */}
            <div className="flex items-center justify-between">
                <div className="flex items-center space-x-4">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                        Filtrar por departamento:
                    </label>
                    <select
                        value={departmentId || selectedDepartment}
                        onChange={(e) => setSelectedDepartment(e.target.value)}
                        disabled={!!departmentId}
                        className={`block w-64 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-blue-500 ${departmentId ? 'bg-gray-100 dark:bg-gray-700 cursor-not-allowed opacity-50' : ''}`}
                    >
                        <option value="">Todos los departamentos</option>
                        {departments.map(dept => (
                            <option key={dept.id} value={dept.id}>
                                {dept.name}
                            </option>
                        ))}
                    </select>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                        Filtrar por empleado:
                    </label>
                    <select
                        value={employeeId || selectedEmployee}
                        onChange={(e) => setSelectedEmployee(e.target.value)}
                        disabled={!!employeeId}
                        className={`block w-64 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-blue-500 ${employeeId ? 'bg-gray-100 dark:bg-gray-700 cursor-not-allowed opacity-50' : ''}`}
                    >
                        <option value="">Todos los empleados</option>
                        {employees.map(emp => (
                            <option key={emp.id} value={emp.id}>
                                {emp.name} ({emp.dni})
                            </option>
                        ))}
                    </select>
                    <label className="flex items-center space-x-2 cursor-pointer">
                        <input
                            type="checkbox"
                            checked={showOnlyDelays}
                            onChange={(e) => setShowOnlyDelays(e.target.checked)}
                            className="rounded border-gray-300 dark:border-gray-600 text-orange-600 dark:text-orange-400 focus:ring-orange-500 dark:focus:ring-orange-400"
                        />
                        <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Solo retrasos</span>
                    </label>
                    <label className="flex items-center space-x-2 cursor-pointer">
                        <input
                            type="checkbox"
                            checked={showOnlyOutsideSchedule}
                            onChange={(e) => setShowOnlyOutsideSchedule(e.target.checked)}
                            className="rounded border-gray-300 dark:border-gray-600 text-red-600 dark:text-red-400 focus:ring-red-500 dark:focus:ring-red-400"
                        />
                        <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Solo fuera de horario</span>
                    </label>
                </div>

                <Button
                    variant="primary"
                    onClick={() => openManualPunchForm(selectedEmployee)}
                    className="flex items-center space-x-2"
                >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <span>Fichaje Manual</span>
                </Button>
            </div>

            {/* Línea de tiempo */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
                {/* Encabezado con horas */}
                <div className="relative mb-6">
                    <div className="flex justify-between text-xs text-gray-500 mb-2">
                        {hours.map(hour => (
                            <div key={`hour-label-${hour}`} className="flex-1 text-center">
                                {hour.toString().padStart(2, '0')}:00
                            </div>
                        ))}
                    </div>
                    {/* Línea principal */}
                    <div className="relative h-2 bg-gray-200 dark:bg-gray-700 rounded-full">
                        {/* Marcas de hora */}
                        {hours.map(hour => (
                            <div
                                key={`hour-mark-${hour}`}
                                className="absolute top-0 bottom-0 w-px bg-gray-400"
                                style={{ left: `${(hour / 24) * 100}%` }}
                            />
                        ))}
                    </div>
                </div>

                {/* Líneas de tiempo por empleado y fecha */}
                {employeesToShow.length === 0 ? (
                    <div className="text-center py-8 text-gray-500">
                        No hay registros de fichaje para la fecha seleccionada.
                    </div>
                ) : (
                    <div className="space-y-8">
                        {employeesToShow.map(employee => {
                            const uniqueDates = getUniqueDates(employee.id);

                            return (
                                <div key={employee.id} className="border-b border-gray-200 pb-6 last:border-b-0">
                                    {/* Nombre del empleado */}
                                    <div className="mb-4">
                                        <span className="font-medium text-gray-900 dark:text-white text-lg">
                                            {employee.name} ({employee.dni})
                                        </span>
                                    </div>

                                    {/* Mostrar cada fecha con sus fichajes */}
                                    {uniqueDates.length === 0 ? (
                                        <div className="text-center py-4 text-gray-500 bg-gray-50 rounded">
                                            No hay registros para este empleado en el rango de fechas.
                                        </div>
                                    ) : (
                                        <div className="space-y-4">
                                            {uniqueDates.map(dateString => {
                                                const entriesForDate = entriesByEmployeeAndDate[employee.id]?.[dateString] || [];
                                                const isMainDate = isSelectedDate(dateString);

                                                console.log(`🔍 DEBUG render - Procesando fecha ${dateString} para empleado ${employee.id}:`, {
                                                    entriesCount: entriesForDate.length,
                                                    entries: entriesForDate.map(e => ({
                                                        id: e.id,
                                                        type: e.type,
                                                        timestamp: e.timestamp,
                                                        hour: new Date(e.timestamp).getHours(),
                                                        date: new Date(e.timestamp).toISOString().split('T')[0]
                                                    })),
                                                    isMainDate,
                                                    isSelectedDate: isSelectedDate(dateString),
                                                    selectedDateProp: from
                                                });

                                                return (
                                                    <div key={dateString} className={`rounded-lg p-4 ${isMainDate ? 'bg-blue-50 dark:bg-blue-900/30 border-2 border-blue-200 dark:border-blue-800' : 'bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700'}`}>
                                                        {/* Encabezado de fecha */}
                                                        <div className="mb-3">
                                                            <span className={`font-medium ${isMainDate ? 'text-blue-900 dark:text-blue-200' : 'text-gray-700 dark:text-gray-300'}`}>
                                                                {formatDate(dateString)}
                                                                {isMainDate && (
                                                                    <span className="ml-2 text-xs bg-blue-600 dark:bg-blue-700 text-white px-2 py-1 rounded-full">
                                                                        Fecha seleccionada
                                                                    </span>
                                                                )}
                                                            </span>
                                                        </div>

                                                        {/* Línea de tiempo del empleado para esta fecha */}
                                                        <div className="relative h-12 bg-white dark:bg-gray-700 rounded-full border border-gray-300 dark:border-gray-600">
                                                            {/* Indicador de ausencia */}
                                                            {(() => {
                                                                const schedulesForDate = (window as any).schedulesByDateAndEmployee?.[employee.id]?.[dateString] || [];
                                                                if (schedulesForDate.length === 0 || entriesForDate.length > 0) return null;

                                                                const now = new Date();
                                                                const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
                                                                const scheduleDate = new Date(dateString);
                                                                const isToday = scheduleDate.getTime() === today.getTime();

                                                                if (!isToday) return null;

                                                                // Verificar si ha pasado el tiempo de gracia
                                                                const hasAbsence = schedulesForDate.some((schedule: Schedule) => {
                                                                    const [startHour, startMinute] = schedule.startTime.split(':').map(Number);
                                                                    const startMinutes = startHour * 60 + startMinute;
                                                                    const graceInNoLateAfterStartMin = schedule.graceInNoLateAfterStartMin || 0;
                                                                    const noLateUntilMinutes = startMinutes + graceInNoLateAfterStartMin;

                                                                    const currentMinutes = now.getHours() * 60 + now.getMinutes();
                                                                    return currentMinutes > noLateUntilMinutes;
                                                                });

                                                                if (!hasAbsence) return null;

                                                                return (
                                                                    <div className="absolute inset-0 flex items-center justify-center">
                                                                        <span className="text-xs text-red-600 font-medium bg-red-100 px-3 py-1 rounded-full animate-pulse">
                                                                            🔴 Ausente
                                                                        </span>
                                                                    </div>
                                                                );
                                                            })()}

                                                            {/* Indicador de día de descanso o sin turno */}
                                                            {((window as any).schedulesByDateAndEmployee?.[employee.id]?.[dateString] || []).length === 0 && (
                                                                <div className="absolute inset-0 flex items-center justify-center">
                                                                    <span className="text-xs text-gray-500 font-medium bg-gray-100 dark:bg-gray-700 px-3 py-1 rounded-full">
                                                                        {entriesForDate.length === 0 ? '🏖️ Día de descanso' : '⚠️ Sin Turno'}
                                                                    </span>
                                                                </div>
                                                            )}

                                                            {/* Horarios asignados (turnos) - mostrar en la fecha seleccionada y en días con fichajes */}
                                                            {(isMainDate || entriesForDate.length > 0) && ((window as any).schedulesByDateAndEmployee?.[employee.id]?.[dateString] || [])?.map((schedule: Schedule) => {
                                                                const crossesMidnight = scheduleCrossesMidnight(schedule);
                                                                let startPosition = timeToPositionUTC(schedule.startTime);
                                                                let endPosition = timeToPositionUTC(schedule.endTime);
                                                                let width = endPosition - startPosition;
                                                                const isReference = schedule.isReference || false;

                                                                // Si el horario cruza medianoche, mostrarlo desde la hora de inicio hasta el final del día
                                                                if (crossesMidnight) {
                                                                    // Mantener la posición de inicio, pero extender hasta el final del día (100%)
                                                                    endPosition = 100;
                                                                    width = endPosition - startPosition;
                                                                }

                                                                return (
                                                                    <div
                                                                        key={`schedule-${employee.id}-${schedule.id}-${dateString}`}
                                                                        className={`absolute top-1 bottom-1 rounded-md border-2 ${isReference
                                                                            ? 'border-dashed border-gray-300 dark:border-gray-700'
                                                                            : 'border-solid border-gray-400 dark:border-gray-600'
                                                                            }`}
                                                                        style={{
                                                                            left: `${startPosition}%`,
                                                                            width: `${width}%`,
                                                                            backgroundColor: schedule.color || '#e5e7eb',
                                                                            opacity: isMainDate
                                                                                ? (isReference ? 0.4 : 0.75)
                                                                                : (isReference ? 0.25 : 0.5)
                                                                        }}
                                                                        title={`${schedule.name}: ${schedule.startTime} - ${schedule.endTime}${crossesMidnight ? ' (cruza medianoche)' : ''}${isReference ? ' (turno predefinido)' : ''}`}
                                                                    >
                                                                        <div className={`text-xs text-center font-medium truncate px-1 ${isMainDate
                                                                            ? (isReference ? 'text-gray-500 dark:text-gray-400' : 'text-gray-700 dark:text-gray-300')
                                                                            : (isReference ? 'text-gray-400' : 'text-gray-600 dark:text-gray-400')
                                                                            }`}>
                                                                            {schedule.name}
                                                                            {crossesMidnight && (
                                                                                <span className="ml-1">🌙</span>
                                                                            )}
                                                                            {isReference && (
                                                                                <span className="ml-1 text-xs">📅</span>
                                                                            )}
                                                                        </div>
                                                                    </div>
                                                                );
                                                            })}

                                                            {/* Registros de fichaje para esta fecha */}
                                                            {entriesForDate.map(entry => {
                                                                // Usar campos de evaluación de TimeEntry (calculados en el backend con periodo de gracia)
                                                                const isOutOfSchedule = entry.isOutOfSchedule || false;
                                                                const lateMinutes = entry.lateMinutes || 0;
                                                                const earlyLeaveMinutes = entry.earlyLeaveMinutes || 0;
                                                                const hasDelay = lateMinutes > 0;

                                                                console.log(`🔍 DEBUG TimelineView - Evaluación de fichaje ${entry.id} (${entry.type}):`, {
                                                                    timestamp: entry.timestamp,
                                                                    isOutOfSchedule,
                                                                    lateMinutes,
                                                                    earlyLeaveMinutes,
                                                                    outOfScheduleReason: entry.outOfScheduleReason,
                                                                    scheduleId: entry.scheduleId
                                                                });

                                                                // Verificar si pertenece a un turno nocturno del día anterior
                                                                const entryTime = new Date(entry.timestamp);
                                                                const entryHour = entryTime.getHours();
                                                                let belongsToPreviousNightShift = false;

                                                                const previousDay = new Date(entryTime);
                                                                previousDay.setDate(previousDay.getDate() - 1);
                                                                const previousDateString = previousDay.toISOString().split('T')[0];
                                                                const previousDaySchedules = (window as any).schedulesByDateAndEmployee?.[employee.id]?.[previousDateString] || [];

                                                                const hasNightShiftPreviousDay = previousDaySchedules.some((schedule: Schedule) => {
                                                                    const [startHour] = schedule.startTime.split(':').map(Number);
                                                                    const [endHour] = schedule.endTime.split(':').map(Number);
                                                                    return endHour < startHour;
                                                                });

                                                                if (hasNightShiftPreviousDay) {
                                                                    if (entryHour < 6) {
                                                                        belongsToPreviousNightShift = true;
                                                                    } else if (entry.type === 'OUT') {
                                                                        const previousDayEntries = entriesByEmployeeAndDate[employee.id]?.[previousDateString] || [];
                                                                        const hasNightShiftEntryPreviousDay = previousDayEntries.some((e: TimeEntry) => {
                                                                            const eHour = new Date(e.timestamp).getHours();
                                                                            return (e.type === 'IN' || e.type === 'RESUME') && eHour >= 22;
                                                                        });

                                                                        if (hasNightShiftEntryPreviousDay) {
                                                                            belongsToPreviousNightShift = true;
                                                                        }
                                                                    }
                                                                }

                                                                const isWithinSchedule = !(entry.isOutOfSchedule || false);

                                                                // Aplicar filtros
                                                                if (showOnlyDelays && !hasDelay) {
                                                                    return null;
                                                                }
                                                                if (showOnlyOutsideSchedule && !isOutOfSchedule) {
                                                                    return null;
                                                                }

                                                                const position = timestampToPosition(entry.timestamp);
                                                                const entryDate = new Date(entry.timestamp).toISOString().split('T')[0];

                                                                console.log(`🔍 DEBUG render - Renderizando entry ${entry.id} (${entry.type}) en fecha ${dateString}:`, {
                                                                    entryTimestamp: entry.timestamp,
                                                                    entryDate,
                                                                    entryHour,
                                                                    position,
                                                                    containerDateString: dateString,
                                                                    datesMatch: entryDate === dateString,
                                                                    isWithinSchedule,
                                                                    isOutOfSchedule,
                                                                    lateMinutes,
                                                                    earlyLeaveMinutes
                                                                });

                                                                return (
                                                                    <div
                                                                        key={`entry-${entry.id}-${dateString}`}
                                                                        className={`group absolute top-0 bottom-0 w-3 rounded-full ${getTypeColor(entry.type)} border-2 border-white shadow-sm transform -translate-x-1/2 cursor-pointer hover:scale-125 transition-transform ${isOutOfSchedule ? 'ring-2 ring-red-400 ring-offset-1' : ''}`}
                                                                        style={{ left: `${position}%` }}
                                                                         title={`${getTypeLabel(entry.type)}: ${entry.localTimestamp ? new Date(entry.localTimestamp).toLocaleTimeString('es-ES') : new Date(entry.timestamp).toLocaleTimeString('es-ES')}${isOutOfSchedule ? ' (Fuera de ventana)' : ' (Dentro de ventana)'}${lateMinutes > 0 ? ` (Retraso: ${formatMinutes(lateMinutes)})` : ''}${earlyLeaveMinutes > 0 ? ` (Salida anticipada: ${formatMinutes(earlyLeaveMinutes)})` : ''}`}
                                                                    >
                                                                         {/* Indicador de fuera de ventana */}
                                                                         {isOutOfSchedule && (
                                                                             <div className="absolute -top-1 -right-1 w-2 h-2 bg-red-500 rounded-full border border-white animate-pulse"></div>
                                                                         )}

                                                                         {/* Indicador de dentro de ventana (sin retrasos ni salidas anticipadas) */}
                                                                         {!isOutOfSchedule && lateMinutes === 0 && earlyLeaveMinutes === 0 && (
                                                                             <div className="absolute -top-1 -left-1 w-2 h-2 bg-green-500 rounded-full border border-white"></div>
                                                                         )}

                                                                         {/* Indicador de retraso */}
                                                                         {lateMinutes > 0 && (
                                                                             <div className="absolute -bottom-1 -right-1 w-2 h-2 bg-orange-500 rounded-full border border-white animate-pulse"></div>
                                                                         )}

                                                                         {/* Indicador de salida anticipada */}
                                                                         {earlyLeaveMinutes > 0 && (
                                                                             <div className="absolute -bottom-1 -left-1 w-2 h-2 bg-purple-500 rounded-full border border-white animate-pulse"></div>
                                                                         )}

                                                                        {/* Botón para continuar pausa (solo para registros de tipo BREAK) */}
                                                                        {entry.type === 'BREAK' && (
                                                                            <button
                                                                                onClick={(e) => {
                                                                                    e.preventDefault();
                                                                                    e.stopPropagation();
                                                                                                                                                                        setSelectedBreakEntry(entry);
                                                                                    setContinueBreakTimestamp(new Date().toISOString().slice(0, 16));
                                                                                    setShowContinueBreakModal(true);
                                                                                }}
                                                                                disabled={continuingBreak}
                                                                                className="absolute -top-3 -right-3 w-5 h-5 bg-blue-600 hover:bg-blue-700 text-white rounded-full flex items-center justify-center text-xs font-bold shadow-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed z-30"
                                                                                title="Continuar Pausa"
                                                                                type="button"
                                                                            >
                                                                                {continuingBreak ? '...' : '▶'}
                                                                            </button>
                                                                        )}

                                                                        <div className="absolute -top-6 left-1/2 transform -translate-x-1/2 bg-gray-800 text-white text-xs px-2 py-1 rounded whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity z-10">
                                                                            {getTypeLabel(entry.type, entry.breakType)}
                                                                            {entry.breakType?.isCustom && (
                                                                                <>
                                                                                    <br />
                                                                                    <span className="text-yellow-300">✏️ Tipo personalizado</span>
                                                                                </>
                                                                            )}
                                                                            <br />
                                                                            {entry.localTimestamp ? new Date(entry.localTimestamp).toLocaleTimeString('es-ES') : new Date(entry.timestamp).toLocaleTimeString('es-ES')}
                                                                            <br />
                                                                            <span className="text-xs text-gray-400">
                                                                                Fecha: {entryDate} | Container: {dateString}
                                                                            </span>
                                                                            {belongsToPreviousNightShift && (
                                                                                <>
                                                                                    <br />
                                                                                    <span className="text-purple-300 font-semibold">🌙 Pertenece al turno nocturno del día anterior</span>
                                                                                </>
                                                                            )}
                                                                             {!isOutOfSchedule && lateMinutes === 0 && earlyLeaveMinutes === 0 && (
                                                                                 <>
                                                                                     <br />
                                                                                     <span className="text-green-300 font-semibold">✅ Dentro de ventana</span>
                                                                                 </>
                                                                             )}
                                                                             {isOutOfSchedule && (
                                                                                 <>
                                                                                     <br />
                                                                                     <span className="text-red-300 font-semibold">⚠️ Fuera de ventana</span>
                                                                                 </>
                                                                             )}
                                                                             {lateMinutes > 0 && (
                                                                                <>
                                                                                    <br />
                                                                                    <span className="text-orange-300 font-semibold">⏰ Retraso: {formatMinutes(lateMinutes)}</span>
                                                                                </>
                                                                            )}
                                                                            {earlyLeaveMinutes > 0 && (
                                                                                <>
                                                                                    <br />
                                                                                    <span className="text-purple-300 font-semibold">🚪 Salida anticipada: {formatMinutes(earlyLeaveMinutes)}</span>
                                                                                </>
                                                                            )}
                                                                            {entry.type === 'BREAK' && (
                                                                                <>
                                                                                    <br />
                                                                                    <span className="text-blue-300 font-semibold">▶ Click para continuar</span>
                                                                                </>
                                                                            )}
                                                                        </div>
                                                                    </div>
                                                                );
                                                            })}
                                                        </div>

                                                        {/* Leyenda de tipos de fichaje para esta fecha */}
                                                        {entriesForDate.length > 0 && (
                                                            <div className="mt-3 flex flex-wrap gap-2">
                                                                {(() => {
                                                                    const uniqueTypes = Array.from(new Set(entriesForDate.map(e => e.type)));
                                                                    // Ordenar tipos: primero IN (Entrada), luego OUT (Salida), luego el resto
                                                                    const orderedTypes = uniqueTypes.sort((a, b) => {
                                                                        if (a === 'IN') return -1;
                                                                        if (b === 'IN') return 1;
                                                                        if (a === 'OUT') return -1;
                                                                        if (b === 'OUT') return 1;
                                                                        return 0;
                                                                    });
                                                                    return orderedTypes;
                                                                })().map(type => (
                                                                    <div key={type} className="flex items-center space-x-1">
                                                                        <div className={`w-3 h-3 rounded-full ${getTypeColor(type)}`} />
                                                                        <span className="text-xs text-gray-600">
                                                                            {type === 'BREAK' && entriesForDate.find(e => e.type === 'BREAK')?.breakType
                                                                                ? (
                                                                                    <>
                                                                                        {getTypeLabel(type, entriesForDate.find(e => e.type === 'BREAK')?.breakType)}
                                                                                        {entriesForDate.find(e => e.type === 'BREAK')?.breakType?.isCustom && (
                                                                                            <span className="ml-1">✏️</span>
                                                                                        )}
                                                                                    </>
                                                                                )
                                                                                : getTypeLabel(type)}
                                                                        </span>
                                                                    </div>
                                                                ))}
                                                                <div className="text-xs text-gray-500 ml-2">
                                                                    {entriesForDate.length} fichaje{entriesForDate.length !== 1 ? 's' : ''}
                                                                </div>
                                                            </div>
                                                        )}

                                                        {/* Resumen de horas */}
                                                        <div className="mt-3 text-xs text-gray-600">
                                                            <div className="flex flex-wrap gap-4">
                                                                {/* Horas brutas (basadas en turnos) */}
                                                                {((window as any).schedulesByDateAndEmployee?.[employee.id]?.[dateString] || [])?.length > 0 && (
                                                                    <div className="flex items-center space-x-1">
                                                                        <span className="font-medium">Horas brutas:</span>
                                                                        <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded">
                                                                            {(() => {
                                                                                                                                                                                                                                                const schedulesForDate = (window as any).schedulesByDateAndEmployee?.[employee.id]?.[dateString] || [];
                                                                                                                                                                if (schedulesForDate.length > 0) {
                                                                                    
                                                                                }
                                                                                const grossHours = calculateGrossHours(schedulesForDate);
                                                                                                                                                                return formatMinutes(grossHours);
                                                                            })()}
                                                                        </span>
                                                                    </div>
                                                                )}

                                                                {/* Horas netas (basadas en fichajes) */}
                                                                <div className="flex items-center space-x-1">
                                                                    <span className="font-medium">Horas netas:</span>
                                                                    <span className="bg-green-100 text-green-800 px-2 py-1 rounded">
                                                                        {(() => {
                                                                            // Para turnos nocturnos, incluir registros del día siguiente cuando hay un IN o RESUME después de las 22:00
                                                                            // NO incluir registros del día anterior cuando hay un OUT antes de las 06:00 (para evitar contar las mismas horas en ambos días)
                                                                            let entriesForNetHours = [...entriesForDate];

                                                                            // Verificar si hay un IN o RESUME después de las 22:00 (turno nocturno que continúa al día siguiente)
                                                                            const hasLateEntry = entriesForDate.some(e => {
                                                                                const hour = new Date(e.timestamp).getHours();
                                                                                return (e.type === 'IN' || e.type === 'RESUME') && hour >= 22;
                                                                            });

                                                                            if (hasLateEntry) {
                                                                                // Incluir registros del día siguiente (especialmente el OUT)
                                                                                const nextDay = new Date(dateString);
                                                                                nextDay.setDate(nextDay.getDate() + 1);
                                                                                const nextDateString = nextDay.toISOString().split('T')[0];
                                                                                const nextDayEntries = entriesByEmployeeAndDate[employee.id]?.[nextDateString] || [];
                                                                                entriesForNetHours = [...entriesForNetHours, ...nextDayEntries];

                                                                                
                                                                            }

                                                                            return formatMinutes(calculateNetHours(entriesForNetHours));
                                                                        })()}
                                                                    </span>
                                                                </div>

                                                                {/* Tiempo de pausa */}
                                                                <div className="flex items-center space-x-1">
                                                                    <span className="font-medium">Tiempo de pausa:</span>
                                                                    <span className="bg-yellow-100 text-yellow-800 px-2 py-1 rounded">
                                                                        {formatMinutes(calculateBreakMinutes(entriesForDate))}
                                                                    </span>
                                                                </div>

                                                                {/* Total de retrasos */}
                                                                {(() => {
                                                                    const totalDelayMinutes = entriesForDate.reduce((total, entry) => {
                                                                        return total + (entry.lateMinutes || 0);
                                                                    }, 0);

                                                                    return totalDelayMinutes > 0 && (
                                                                        <div className="flex items-center space-x-1">
                                                                            <span className="font-medium">Total de retrasos:</span>
                                                                            <span className="bg-orange-100 text-orange-800 px-2 py-1 rounded flex items-center space-x-1">
                                                                                <span className="w-2 h-2 bg-orange-500 rounded-full animate-pulse"></span>
                                                                                <span>{formatMinutes(totalDelayMinutes)}</span>
                                                                            </span>
                                                                        </div>
                                                                    );
                                                                })()}

                                                                {/* Fichajes fuera de horario */}
                                                                {((window as any).schedulesByDateAndEmployee?.[employee.id]?.[dateString] || [])?.length > 0 && entriesForDate.length > 0 && (
                                                                    <div className="flex items-center space-x-1">
                                                                        <span className="font-medium">Fuera de horario:</span>
                                                                        {(() => {
                                                                            const outsideScheduleCount = entriesForDate.filter(entry => {
                                                                                return entry.isOutOfSchedule || false;
                                                                            }).length;

                                                                            return outsideScheduleCount > 0 ? (
                                                                                <span className="bg-red-100 text-red-800 px-2 py-1 rounded flex items-center space-x-1">
                                                                                    <span className="w-2 h-2 bg-red-500 rounded-full animate-pulse"></span>
                                                                                    <span>{outsideScheduleCount} fichaje{outsideScheduleCount !== 1 ? 's' : ''}</span>
                                                                                </span>
                                                                            ) : (
                                                                                <span className="bg-gray-100 text-gray-600 px-2 py-1 rounded">
                                                                                    Ninguno
                                                                                </span>
                                                                            );
                                                                        })()}
                                                                    </div>
                                                                )}

                                                                {/* Información adicional */}
                                                                {((window as any).schedulesByDateAndEmployee?.[employee.id]?.[dateString] || [])?.length > 0 && (
                                                                    <div className="flex items-center space-x-1">
                                                                        <span className="text-gray-500">
                                                                            {(() => {
                                                                                const schedulesForDate = (window as any).schedulesByDateAndEmployee?.[employee.id]?.[dateString] || [];
                                                                                return `${schedulesForDate.length} turno${schedulesForDate.length !== 1 ? 's' : ''}`;
                                                                            })()}
                                                                        </span>
                                                                        {((window as any).schedulesByDateAndEmployee?.[employee.id]?.[dateString] || [])?.some((s: Schedule) => scheduleCrossesMidnight(s)) && (
                                                                            <span className="text-blue-600">
                                                                                <span className="inline-block w-2 h-2 bg-blue-600 rounded-full mr-1"></span>
                                                                                Cruza medianoche
                                                                            </span>
                                                                        )}
                                                                        {!isMainDate && (
                                                                            <span className="text-gray-500">
                                                                                <span className="inline-block w-2 h-2 bg-gray-400 rounded-full mr-1"></span>
                                                                                Predefinido
                                                                            </span>
                                                                        )}
                                                                    </div>
                                                                )}
                                                            </div>
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>

            {/* Información adicional */}
            <div className="bg-blue-50 border border-blue-200 text-blue-700 px-4 py-3 rounded">
                <div className="text-sm">
                    <strong>Información:</strong>
                    <ul className="mt-1 ml-4 list-disc space-y-1">
                        <li>Los rectángulos coloreados representan los turnos asignados (visibles solo en la fecha seleccionada)</li>
                        <li>Los círculos indican los registros de fichaje reales</li>
                        <li><strong>Horas brutas:</strong> Total de horas asignadas según los turnos predefinidos del día</li>
                        <li><strong>Horas netas:</strong> Total de horas realmente trabajadas según fichajes de entrada/salida</li>
                        <li><strong>Tiempo de pausa:</strong> Total de tiempo de pausa según fichajes de pausa/reanudar</li>
                        <li><strong>Fichajes fuera de horario:</strong> Se muestran con un anillo rojo y un punto rojo parpadeante</li>
                        <li>Los turnos que cruzan medianoche se muestran ocupando todo el ancho con el símbolo 🌙</li>
                        <li>Los turnos predefinidos se muestran con menor opacidad y símbolo 📅</li>
                        <li>Los fichajes están separados por fechas para mayor claridad</li>
                        <li>La fecha seleccionada aparece resaltada en azul con los turnos asignados</li>
                        <li>Pasa el cursor sobre los elementos para ver más detalles</li>
                    </ul>
                </div>
            </div>

            {/* Modal para fichaje manual */}
            {showManualPunch && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md">
                        <div className="flex justify-between items-center mb-4">
                            <h3 className="text-lg font-semibold text-gray-900">
                                Fichaje Manual
                            </h3>
                            <button
                                onClick={() => setShowManualPunch(false)}
                                className="text-gray-400 hover:text-gray-600 transition-colors"
                            >
                                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        </div>

                        {punchError && (
                            <div className="mb-4 bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded">
                                {punchError}
                            </div>
                        )}

                        <div className="space-y-4">
                            {/* Selector de empleado */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                    Empleado
                                </label>
                                <select
                                    value={manualPunchData.employeeId}
                                    onChange={(e) => setManualPunchData({ ...manualPunchData, employeeId: e.target.value })}
                                    className="block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-blue-500"
                                    required
                                >
                                    <option value="">Seleccionar empleado...</option>
                                    {employees.map(emp => (
                                        <option key={`manual-emp-${emp.id}`} value={emp.id}>
                                            {emp.name} ({emp.dni})
                                        </option>
                                    ))}
                                </select>
                            </div>

                            {/* Tipo de fichaje */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                    Tipo de Fichaje
                                </label>
                                <div className="flex space-x-4">
                                    <label className="flex items-center">
                                        <input
                                            type="radio"
                                            value="IN"
                                            checked={manualPunchData.type === 'IN'}
                                            onChange={(e) => setManualPunchData({ ...manualPunchData, type: e.target.value as 'IN' | 'OUT' })}
                                            className="mr-2"
                                        />
                                        <span className="text-sm text-gray-700">Entrada</span>
                                    </label>
                                    <label className="flex items-center">
                                        <input
                                            type="radio"
                                            value="OUT"
                                            checked={manualPunchData.type === 'OUT'}
                                            onChange={(e) => setManualPunchData({ ...manualPunchData, type: e.target.value as 'IN' | 'OUT' })}
                                            className="mr-2"
                                        />
                                        <span className="text-sm text-gray-700">Salida</span>
                                    </label>
                                </div>
                            </div>

                            {/* Fecha */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                    Fecha
                                </label>
                                <input
                                    type="date"
                                    value={manualPunchData.date}
                                    onChange={(e) => setManualPunchData({ ...manualPunchData, date: e.target.value })}
                                    className="block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-blue-500"
                                    required
                                />
                            </div>

                            {/* Hora */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                    Hora
                                </label>
                                <input
                                    type="time"
                                    value={manualPunchData.time}
                                    onChange={(e) => setManualPunchData({ ...manualPunchData, time: e.target.value })}
                                    className="block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-blue-500"
                                    required
                                />
                            </div>
                        </div>

                        <div className="flex justify-end space-x-3 mt-6">
                            <Button
                                variant="secondary"
                                onClick={() => setShowManualPunch(false)}
                                disabled={submittingPunch}
                            >
                                Cancelar
                            </Button>
                            <Button
                                variant="primary"
                                onClick={handleManualPunch}
                                disabled={submittingPunch}
                                className="flex items-center space-x-2"
                            >
                                {submittingPunch && (
                                    <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                    </svg>
                                )}
                                <span>{submittingPunch ? 'Guardando...' : 'Registrar Fichaje'}</span>
                            </Button>
                        </div>
                    </div>
                </div>
            )}

            {/* Modal para continuar pausa */}
            {showContinueBreakModal && selectedBreakEntry && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md">
                        <div className="flex justify-between items-center mb-4">
                            <h3 className="text-lg font-semibold text-gray-900">
                                Continuar Pausa
                            </h3>
                            <button
                                onClick={() => {
                                    setShowContinueBreakModal(false);
                                    setSelectedBreakEntry(null);
                                    setContinueBreakError(null);
                                }}
                                className="text-gray-400 hover:text-gray-600 transition-colors"
                            >
                                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        </div>

                        {continueBreakError && (
                            <div className="mb-4 bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded">
                                {continueBreakError}
                            </div>
                        )}

                        <div className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                    Empleado
                                </label>
                                <div className="text-sm text-gray-600">
                                    {selectedBreakEntry.employee.name} ({selectedBreakEntry.employee.dni})
                                </div>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                    Tipo de Pausa
                                </label>
                                <div className="text-sm text-gray-600">
                                    {selectedBreakEntry.breakType?.name || 'Pausa'}
                                    {selectedBreakEntry.breakType?.isCustom && (
                                        <span className="ml-2 text-yellow-600">
                                            ({selectedBreakEntry.breakType.customName})
                                        </span>
                                    )}
                                </div>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                    Fecha y Hora de la Pausa
                                </label>
                                <div className="text-sm text-gray-600">
                                    {new Date(selectedBreakEntry.timestamp).toLocaleString('es-ES')}
                                </div>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                    Fecha y Hora de Reanudación
                                </label>
                                <input
                                    type="datetime-local"
                                    value={continueBreakTimestamp}
                                    onChange={(e) => setContinueBreakTimestamp(e.target.value)}
                                    className="block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-blue-500"
                                    required
                                />
                            </div>
                        </div>

                        <div className="flex justify-end space-x-3 mt-6">
                            <Button
                                variant="secondary"
                                onClick={() => {
                                    setShowContinueBreakModal(false);
                                    setSelectedBreakEntry(null);
                                    setContinueBreakError(null);
                                }}
                                disabled={continuingBreak}
                            >
                                Cancelar
                            </Button>
                            <Button
                                variant="primary"
                                onClick={() => handleContinueBreak(selectedBreakEntry, continueBreakTimestamp)}
                                disabled={continuingBreak}
                                className="flex items-center space-x-2"
                            >
                                {continuingBreak && (
                                    <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3.7.938l3-2.647z"></path>
                                    </svg>
                                )}
                                <span>{continuingBreak ? 'Continuando...' : 'Continuar Pausa'}</span>
                            </Button>
                        </div>
                    </div>
                </div>
            )}

            {/* Mensaje de error para continuar pausa */}
            {continueBreakError && (
                <div className="fixed bottom-4 right-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded shadow-lg z-50 flex items-center space-x-2">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <span>{continueBreakError}</span>
                    <button
                        onClick={() => setContinueBreakError(null)}
                        className="ml-2 text-red-400 hover:text-red-600"
                    >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                </div>
            )}
        </div>
    );
};

export default TimelineView;